import { differenceInDays, differenceInCalendarDays } from "date-fns";
import { format, utcToZonedTime } from "date-fns-tz";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDomainConfig } from "../../config/config";
import { OrderSummaryContext } from "../../contexts/order-summary-context";
import { useCart } from "../../hooks/useCart";
import { ReactComponent as CheckIcon } from '../../images/global/check.svg';
import { ABContext } from "../../contexts/ab-context";
import { formatCurrency, getGeo } from "../../utils/formatCurrency";

const SubTotal = (props) => {
  const config = getDomainConfig();
  const { t } = useTranslation('common');

  /* LOADING THE BRANDED SCSS */
  const [styles, setStyles] = useState(false);

  useEffect(() => {
    const getStyles = async (theFileName, theFileEnd) => {
      if (config.brand) {
        let moduleStyles = false;
        try {
          moduleStyles = await import(
            "./" + theFileName + config.brand + theFileEnd
          );
          setStyles(moduleStyles);
        } catch (e) {
          console.error(
            `Styles for ${theFileName} could not be loaded, ${e.message}`
          );
        }
      }
    };

    // Specify file name and end of file name here.
    getStyles("SubTotal", ".module.scss");
  }, [config.brand]);

  /* END LOADING THE BRANDED SCSS */

  const abContext = useContext(ABContext);

  // CRO-1381-checkout-redesign
  const [isCro1381, setIsCro1381] = useState(false);

  useEffect(() => {
    if ((config.brand !== 'Sw') && abContext.cro1381 !== undefined) setIsCro1381(abContext.cro1381.variant);
  }, [abContext]);
  // END CRO-1381-checkout-redesign

  const { orderSummary, setOrderSummary } = useContext(OrderSummaryContext);
  const [deliveryCharges, setDeliveryCharges] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [giftCardAmount, setGiftCardAmount] = useState(0);
  const [orderTotal, setOrderTotal] = useState(0);
  const [amountToPay, setAmountToPay] = useState(0);
  const { isSuccess: cartIsSuccess, data: cartContents } = useCart();
  let cartMeta = [];

  const [hotelStayFromDay, setHotelStayFromDay] = useState('');
  const [hotelStayFrom, setHotelStayFrom] = useState('');
  const [hotelStayToDay, setHotelStayToDay] = useState('');
  const [hotelStayTo, setHotelStayTo] = useState('');
  const [hotelTotalPrice] = useState([]);
  const [hotelRooms] = useState([]);
  const [adultsQty,setAdultsQty] = useState(0);
  const [childrenQty, setChildrenQty] = useState(0);
  const [infantsQty, setInfantsQty] = useState(0);

  const [transportProvider, setTransportProvider] = useState('');
  const [transportOutbound, setTransportOutbound] = useState('');
  const [transportInbound, setTransportInbound] = useState('');

  const [stayInNights, setStayInNights] = useState('');
  const [parkTickets, setParkTickets] = useState('');
  const [hotelName, setHotelName] = useState('');

  const [tickets, setTickets] = useState([]);
  const [ticketsTotalPrice] = useState([])

  const [diningTotal] = useState([]);
  const [diningTitle, setDiningTitle] = useState('');

  const [freeGifts] = useState([]);

  const [taxMessage, setTaxMessage] = useState('');

  const [ticketAdultsQty,setTicketAdultsQty] = useState(0);
  const [ticketChildrenQty,setTicketChildrenQty] = useState(0);

  const geo = getGeo();

  if (cartIsSuccess) {
    cartMeta = cartContents.meta;
  }

  // Update delivery and discounts if they's selected/updated.
  useEffect(() => {

    if (orderSummary.deliveryCharges) {
      setDeliveryCharges(
        parseFloat(orderSummary.deliveryCharges.price)
      );
    }
    const totalDiscount = parseFloat(cartMeta.total_discount).toFixed(2);
    setDiscountAmount(totalDiscount);

    const giftCardDeduction = parseFloat(cartMeta.total_gift_card_balance);
    setGiftCardAmount(giftCardDeduction)

    let sumOrderTotal = 0;
    if (cartIsSuccess) {
      sumOrderTotal = parseFloat(cartMeta.total_price);
      if (orderSummary.deliveryCharges) {
        sumOrderTotal += parseFloat(
          orderSummary.deliveryCharges.price
        );
      }
      if (totalDiscount) {
        sumOrderTotal -= parseFloat(totalDiscount);
      }
      if (cartMeta.total_gift_card_balance > 0) {
        sumOrderTotal -= parseFloat(cartMeta.total_gift_card_balance);
      }
      if (+cartMeta?.total_hotel_tax > 0) {
        sumOrderTotal += +cartMeta.total_hotel_tax;
      }
      setOrderTotal(sumOrderTotal < 0 ? 0 : sumOrderTotal.toFixed(2));
      setAmountToPay(sumOrderTotal < 0 ? 0 : sumOrderTotal.toFixed(2));
    }

    if (orderSummary.paymentOption) {
      // If deposit.
      if (orderSummary.paymentOption === "deposit") {
        let deposit = +cartMeta.payment_options.deposittotal - +cartMeta.total_gift_card_balance;

        if (+cartMeta.total_gift_card_balance >= +cartMeta.payment_options.deposittotal) {
          setAmountToPay(0);
        } else {
          setAmountToPay(deposit.toFixed(2));
        }
      } else if (orderSummary.paymentOption === "non-refundable-deposit") {
        setAmountToPay(cartMeta.payment_options.non_refundable_deposit?.amount.toFixed(2));
      } else {
        setAmountToPay(sumOrderTotal < 0 ? 0 : sumOrderTotal.toFixed(2));
      }
    }
  }, [orderSummary, cartIsSuccess]);

  let defaultClass = styles.amountDetails; // The generic class (in css modules format)
  let classes = defaultClass; // The classes list to show

  if (props.className) {
    // If any extra classes come through via props, turn them into css modules format too
    classes =
      classes +
      " " +
      props.className
        .split(" ")
        .map((thisStyle) => {
          return styles[thisStyle];
        })
        .join(" ");
  }

  useEffect(() => {
    setOrderSummary({...orderSummary,total: amountToPay});
  },[amountToPay, cartContents, cartIsSuccess])

  useEffect(() => {
    if (cartIsSuccess) {
      const cartTickets = cartContents.data.tickets;
      if (cartMeta.is_hotel_cart && config.brand === 'Atd') {
        let adultQty = 0;
        let childQty = 0;
        let infantQty = 0;

        cartTickets.map((cartItem) => {
          if (cartItem.tags.some((tag) => tag.includes("rooms"))) {
            setHotelStayFromDay(
              format(utcToZonedTime(new Date(cartItem.attributes['5'][0]),'UTC'),'EEEE',{ timeZone: 'UTC' })
            );
            setHotelStayFrom(
              format(utcToZonedTime(new Date(cartItem.attributes['5'][0]),'UTC'),'dd MMM yyyy',{ timeZone: 'UTC' })
            );
            setHotelStayToDay(
              format(utcToZonedTime(new Date(cartItem.attributes["42"][0]),'UTC'),'EEEE',{ timeZone: 'UTC' })
            );
            setHotelStayTo(
              format(utcToZonedTime(new Date(cartItem.attributes["42"][0]),'UTC'),'dd MMM yyyy',{ timeZone: 'UTC' })
            );
            hotelTotalPrice.push(
              cartItem?.hotel_tax
                ? +cartItem.price + +cartItem.hotel_tax
                : +cartItem.price
            );
            hotelRooms.push(cartItem);
            adultQty += cartItem.attributes["48"][0];
            childQty += cartItem.attributes["49"][0];
            infantQty += cartItem.attributes["87"][0];
            setStayInNights(
              differenceInCalendarDays(
                new Date(cartItem.attributes["42"][0]),
                new Date(cartItem.attributes["5"][0])
              )
            );
            setHotelName(cartItem.data.attraction_title);

            if (+cartItem.internal.supplier_id === 133) {
              if (cartItem.hasOwnProperty("room_package_type")) {
                if (cartItem.room_package_type.includes("ROOM_TICKET_GEN")) {
                  let daysCart;

                  // Les Village Nature is always a 2 day ticket
                  if (cartItem.data.attraction_id === "242389") {
                    daysCart = 2;
                  } else {
                    daysCart =
                      differenceInDays(
                        new Date(cartItem.attributes["42"][0]),
                        new Date(cartItem.attributes["5"][0])
                      ) + 1;
                  }

                  setParkTickets(`${daysCart}`);
                }
              }
            }

            if (cartItem.data?.transport_price_from) {
              if (Object.keys(cartItem.data.transport_price_from).length > 0) {
                setTransportProvider(
                  cartItem.data.transport_price_from.inbound.carrier
                );
                setTransportOutbound(
                  `${format(utcToZonedTime(new Date(cartItem.data.transport_price_from.inbound.departure_date),'UTC'),'do MMM yyyy',{ timeZone: 'UTC' })}, ${cartItem.data.transport_price_from.inbound.departure_time}`
                );
                setTransportInbound(
                  `${format(utcToZonedTime(new Date(cartItem.data.transport_price_from.outbound.departure_date),'UTC'),'do MMM yyyy',{ timeZone: 'UTC' })}, ${cartItem.data.transport_price_from.outbound.departure_time}`
                );
              }
            }
          }

          if (cartItem.data.hasOwnProperty("ac")) {
            if (cartItem.data.ac === "Adult") setTicketAdultsQty(cartItem.qty);
            if (cartItem.data.ac === "Child")
              setTicketChildrenQty(cartItem.qty);
          }

          if (cartItem.tags.some((tag) => tag.includes("tickets") && !tag.includes("transport"))) {
            tickets.push(cartItem);
            ticketsTotalPrice.push(+cartItem.price * +cartItem.qty);

            let groupedTickets = {};

            let adults = 0;
            let children = 0;

            tickets.forEach((ticket) => {
              const { title, qty, date, data } = ticket;

              groupedTickets = tickets.reduce(
                (groups, ticket) => {
                  const { title, data } = ticket;
                  if (!groups[title]) {
                    groups[title] = { tickets: [], data: [] };
                  }
                  groups[title].tickets.push(ticket);
                  groups[title].data.push(data);
                  return groups;
                },
                {}
              );
              for (const [title, { tickets, data }] of Object.entries(
                groupedTickets
              )) {
                const filteredTickets = tickets.filter(
                  (ticket) =>
                    ticket.data.ac === "Adult" || ticket.data.ac === "General"
                );
                if (filteredTickets.length > 0) {
                  groupedTickets[title] = {
                    tickets: filteredTickets,
                    data,
                  };
                } else {
                  delete groupedTickets[title];
                }
              }

              if (data.type_ticket === "Adult") {
                adults = qty;
              } else if (data.type_ticket === "Child") {
                children = qty;
              }

              if (data.hasOwnProperty("ac")) {
                if (data.ac === "Adult") adults = qty;
                if (data.ac === "Child") children = qty;
              }

              if (groupedTickets.hasOwnProperty(title)) {
                groupedTickets[title].adults = adults;
                groupedTickets[title].children = children;
                groupedTickets[title].date = date;
                groupedTickets[title].title = title;
              }
              else {
                groupedTickets[title] = {
                  adults: adults,
                  children: children,
                  date: date,
                  title: title,
                };
              }
            });
            setTickets(groupedTickets);
          }

          if (cartItem.tags.some((tag) => tag.includes("dining"))) {
            setDiningTitle(cartItem.internal.seo_title);
            diningTotal.push(+cartItem.price * +cartItem.qty);
          }

          if (cartItem.tags.some((tag) => tag.includes("freebie"))) {
            freeGifts.push(cartItem);
          }
          return null;
        }
        );
        setAdultsQty(adultQty);
        setChildrenQty(childQty);
        setInfantsQty(infantQty);
      }

    }
  }, [cartIsSuccess]);

  return (
    <div className={`${classes} subTotal-sideTableHolder`} id={props.id}>
      <div>
        <table className={styles.table}>
          <tbody>
            {(!cartMeta.is_hotel_cart && config.brand === "Atd") && (
              <tr className={styles.subtotal}>
                <td className={styles.item}>{t("Subtotal")} </td>
                <td className={styles.price}>
                  {cartMeta.total_price && formatCurrency(cartMeta.total_price, geo)}
                </td>
              </tr>
            )}
            {orderSummary.deliveryCharges && (
              <tr className={styles.delivery}>
                <td className={styles.item}>Delivery </td>
                <td className={styles.price}>
                  {props.currency}
                  {deliveryCharges}
                </td>
              </tr>
            )}
            {giftCardAmount > 0 && (
              <tr className={styles.delivery}>
                <td className={styles.item}>Gift card </td>
                <td className={styles.price}>
                  {" "}
                  -
                  {giftCardAmount >= cartMeta.total_price
                    ? formatCurrency(cartMeta.total_price)
                    : formatCurrency(giftCardAmount.toFixed(2), geo)}
                </td>
              </tr>
            )}
            {discountAmount > 0 && (
              <tr className={styles.discount}>
                <td className={styles.item}>Discount </td>
                <td className={styles.price}>
                  -
                  {formatCurrency(discountAmount, geo)}
                </td>
              </tr>
            )}
            {!isCro1381 &&
              <tr className={styles.totalprice} data-testid="total-price">
                <td className={styles.item}>{t("Total")} </td>
                <td className={styles.price}>
                  {formatCurrency(orderTotal, geo) < 0 ? 0 : formatCurrency(orderTotal, geo)}
                </td>
              </tr>
            }

            {(cartMeta.is_hotel_cart && config.brand !== "Sw") ? (
              isCro1381 && orderSummary.paymentOption !== "full" && (
                <tr className={styles.cro1177HotelSub}>
                  <td className={styles.cro1177Headings}>
                    {t("subtotal")}
                  </td>
                  <td className={styles.cro1177Prices}>
                    {formatCurrency(orderTotal, geo) < 0 ? 0 : formatCurrency(orderTotal, geo)}
                  </td>
                </tr>
              )
            ) : (<></>)}

            <tr className={styles.total} data-testid='total-price'>
            <td className={!isCro1381 ? styles.item : styles.cro1381Total}>{!isCro1381
              ? t("total")
              : orderSummary.paymentOption !== "full"
              ? t("total-to-pay-today")
              : t("Total")} </td>
              <td className={!isCro1381 ? styles.price : styles.cro1381Price}>
                {amountToPay && formatCurrency(amountToPay, geo) < 0 ? 0 : amountToPay && formatCurrency(amountToPay, geo)}
              </td>
            </tr>
          </tbody>
        </table>
        {((props.cancellationMessage !== "" && (orderSummary.paymentOption !== "non-refundable-deposit"))) &&(
          <div className={styles.cancellationTextMobileContainer}>
            <span className={styles.cancellationText}>
              <CheckIcon />
              {props.cancellationMessage}
            </span>
          </div>
        )}
        {(!!props.cancellationMessage || !!taxMessage) && !isCro1381 && (
          <div className={styles.pricePersuasions}>
            {taxMessage !== "" && (
              <span className={styles.taxMessage}>{taxMessage}</span>
            )}
            {props.cancellationMessage !== "" && (
              <span className={styles.cancellationText}>
                <CheckIcon />
                {props.cancellationMessage}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubTotal;
